// 極簡 fetch wrapper：bearer token 存 localStorage，401 清 token 導回登入。

const TOKEN_KEY = 'ipo_ls_admin_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
  ) {
    super(`API ${status}`)
  }
}

export interface RequestOptions {
  method?: string
  body?: unknown
  raw?: boolean // 回傳 Response（下載用）
}

/**
 * 組 query string：`undefined` 的鍵一律省略、全空回空字串（不留裸 `?`）。
 *
 * 原本只在 `hosting.ts` 私有；批80 批B2 讓 `ls.ts` 的清單端點也開始收過濾參數，
 * 兩處各寫一份遲早行為分岔（尤其「空值要不要送」這種細節），所以提到這裡共用。
 */
export function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined) sp.set(k, v)
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, raw = false } = opts
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401) {
    setToken(null)
    // 跟著 vite base 走（批80 批E 起是 '/'），別寫死 '/admin/login'。
    if (!location.pathname.endsWith('/login'))
      location.assign(import.meta.env.BASE_URL + 'login')
    throw new ApiError(401, null)
  }
  if (!res.ok) {
    let payload: unknown = null
    try {
      payload = await res.json()
    } catch {
      payload = null
    }
    throw new ApiError(res.status, payload)
  }
  if (raw) return res as unknown as T
  if (res.status === 204) return null as T
  return (await res.json()) as T
}

/**
 * 以**原始 body** 上傳檔案（批153：plugin 版本上傳——LS 後端不收 multipart，
 * `Content-Type: application/octet-stream`、整個檔案就是 body）。401／錯誤處理與 `api()` 相同。
 */
export async function apiUpload<T>(path: string, file: Blob): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(path, { method: 'POST', headers, body: file })
  if (res.status === 401) {
    setToken(null)
    if (!location.pathname.endsWith('/login'))
      location.assign(import.meta.env.BASE_URL + 'login')
    throw new ApiError(401, null)
  }
  if (!res.ok) {
    let payload: unknown = null
    try {
      payload = await res.json()
    } catch {
      payload = null
    }
    throw new ApiError(res.status, payload)
  }
  return (await res.json()) as T
}
